import React, { useEffect, useState } from 'react'
import { Label } from '@/shared/components/ui/label'
import { Slider } from '@/shared/components/ui/slider'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/shared/components/ui/select"
import { AIGenerationBaseDialog } from '@/shared/components/custom/AIGenerationBaseDialog'
import { useStoryboardStore } from '../store/useStoryboardStore'
import { Shot, useShotListStore } from '@/features/shotlist/store/useShotListStore'
import { useGenerateStoryboardMutation } from '../queries/storyboard.ai.queries'
import { useSaveStoryboardMutation, useStoryboardQuery } from '../queries/storyboard.queries'
import { StoryboardGenerationOptions } from '@/features/storyboard/types/storyboard.types'
import { useWorkspace } from '@/features/workspace/stores/workspace.store'
import { useSceneStore } from '@/features/shared/stores/scene.store'
import { SerializedContent } from '@storyverse/shared'

interface StoryboardGenerationDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    shot?: Shot | null // Optional single shot context
}

const visualStyles = [
    "Cinematic",
    "Documentary",
    "Noir",
    "Bright & Colorful",
    "Minimalist",
    "Dramatic"
]

export const StoryboardGenerationDialog: React.FC<StoryboardGenerationDialogProps> = ({
    open,
    onOpenChange,
    shot
}) => {
    const { project } = useWorkspace()
    const { scenes, editor } = useSceneStore()
    const { selectedSceneNode } = useStoryboardStore()
    const selectedSceneId = selectedSceneNode?.id

    const [mode, setMode] = useState<'auto' | 'custom' | null>(null)
    const [frameCount, setFrameCount] = useState<number>(shot ? 1 : 8)
    const [style, setStyle] = useState('')
    const [includeDialogue, setIncludeDialogue] = useState(true)
    const [generateImages, setGenerateImages] = useState(true)
    const [isLoading, setIsLoading] = useState(false)

    // Reset state when dialog opens or shot changes
    useEffect(() => {
        if (open) {
            setMode(null)
            setFrameCount(shot ? 1 : 8)
            setStyle('')
            setIncludeDialogue(true)
        }
    }, [open, shot])

    const { mutateAsync: generateStoryboard } = useGenerateStoryboardMutation()
    const { mutateAsync: saveStoryboard } = useSaveStoryboardMutation(project?.id || '', selectedSceneId || '')
    const { data: existingFrames = [] } = useStoryboardQuery(project?.id || '', selectedSceneId || null)

    const buildGenerationOptions = (
        targetShot: Shot,
        shotList: Shot[],
        sceneContext: SerializedContent[],
        selectedMode: 'auto' | 'custom'
    ): StoryboardGenerationOptions => {
        const shotIndex = shotList.findIndex((item) => item.id === targetShot.id)
        const previousShots = shotIndex > 0 ? shotList.slice(Math.max(0, shotIndex - 2), shotIndex) : []
        const nextShots =
            shotIndex >= 0 ? shotList.slice(shotIndex + 1, Math.min(shotList.length, shotIndex + 3)) : []

        const previousShotIds = new Set(previousShots.map((item) => item.id))
        const nextShotIds = new Set(nextShots.map((item) => item.id))

        return {
            mode: selectedMode,
            frameCount: shot ? (selectedMode === 'custom' ? frameCount : undefined) : undefined,
            style: selectedMode === 'custom' ? style || undefined : undefined,
            includeDialogue: selectedMode === 'custom' ? includeDialogue : undefined,
            generateImages,
            sceneId: selectedSceneId,
            shot: targetShot,
            shotContext: {
                previousShots,
                nextShots,
            },
            frameContext: {
                previousFrames: existingFrames.filter((frame) => previousShotIds.has(frame.shotId)),
                nextFrames: existingFrames.filter((frame) => nextShotIds.has(frame.shotId)),
            },
            sceneContext,
        }
    }

    const handleGenerate = async (selectedMode: 'auto' | 'custom') => {
        if (!selectedSceneId || !editor || !project || !selectedSceneNode) {
            console.error("Missing required data for generation")
            return
        }

        setIsLoading(true)
        // setIsGenerating is removed from store in favor of local loading state derived from mutation

        try {
            const { shotLists } = useShotListStore.getState()
            const shotList = shotLists[selectedSceneId] || []
            const sceneContext = editor.read(
                () => selectedSceneNode.getCurrentSceneContent()
            ) as SerializedContent[]

            if (!sceneContext?.length) {
                throw new Error("Scene context is required to generate storyboard frames")
            }

            console.log(`Generating ${shot ? 'frame for shot' : 'storyboard'}...`)

            if (shot) {
                const options = buildGenerationOptions(shot, shotList, sceneContext, selectedMode)
                const frames = await generateStoryboard(options)
                // Merge: keep frames from other shots, replace frames for this shot with newly generated ones
                const framesForOtherShots = existingFrames.filter(f => f.shotId !== shot.id)
                const mergedFrames = [...framesForOtherShots, ...frames]
                await saveStoryboard(mergedFrames)
            } else {
                const generatedFramesByShot = await Promise.all(
                    shotList.map((targetShot) =>
                        generateStoryboard(
                            buildGenerationOptions(targetShot, shotList, sceneContext, selectedMode)
                        )
                    )
                )

                const flattenedFrames = generatedFramesByShot.flat()
                await saveStoryboard(flattenedFrames)
            }

            onOpenChange(false)
        } catch (error) {
            console.error("Error generating storyboard:", error)
            alert(`Failed to generate storyboard: ${error instanceof Error ? error.message : 'Unknown error'}`)
        } finally {
            setIsLoading(false)
        }
    }

    const reset = () => {
        setMode(null)
        onOpenChange(false)
    }

    return (
        <AIGenerationBaseDialog
            open={open}
            onOpenChange={reset}
            dialogTitle={shot ? `Generate Frame for ${shot.shotType}` : "Generate Storyboard"}
            dialogDescription={shot ? "Choose how to generate this specific shot" : "Choose generation mode"}
            mode={mode}
            onModeChange={setMode}
            isLoading={isLoading}
            loadingTitle={`Generating ${shot ? 'Frame' : 'Storyboard'}...`}
            loadingDescription="Creating visual magic for your scene"
            autoPilotDescription={
                shot
                    ? "Let StoryVerse AI generate the frame based on your shot description automatically."
                    : "Let StoryVerse AI analyze your scene and decide the best shots, angles, and composition automatically."
            }
            onAutoPilotClick={() => handleGenerate('auto')}
            customDirectorDescription={
                shot
                    ? "Add context, specific details, or visual style instructions for this frame."
                    : "You are in control. Customize style, visual tone, and generation details before rolling 'action'."
            }
            customStepDescription="Fine-tune your generation"
            onCustomGenerate={() => handleGenerate('custom')}
            customGenerateLabel={`Generate ${shot ? 'Frame' : 'Storyboard'}`}
        >
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label>Number of Frames ({frameCount})</Label>
                    <Slider
                        min={1} max={20} step={1}
                        value={[frameCount]}
                        onValueChange={(v) => setFrameCount(v[0])}
                    />
                    {shot && (
                        <p className="text-xs text-muted-foreground">
                            Generates {frameCount} variations for this shot.
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <Label>Visual Style</Label>
                    <Select value={style} onValueChange={setStyle}>
                        <SelectTrigger>
                            <SelectValue placeholder="Select a visual style" />
                        </SelectTrigger>
                        <SelectContent>
                            {visualStyles.map(s => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div className="flex items-center space-x-2 pt-2">
                    <input type="checkbox" id="genImages" checked={generateImages} onChange={e => setGenerateImages(e.target.checked)} className="h-4 w-4" />
                    <Label htmlFor="genImages">Generate AI Images</Label>
                </div>
            </div>
        </AIGenerationBaseDialog>
    )
}

export default StoryboardGenerationDialog
